import { useMemo, useState } from "react";
import { Link } from "wouter";
import {
  ConnectComponentsProvider,
  ConnectBalances,
  ConnectPayouts,
  ConnectPayments,
  ConnectAccountManagement,
  ConnectDocuments,
} from "@stripe/react-connect-js";
import Header from "@/components/Header";
import { ChevronLeft, AlertCircle } from "lucide-react";
import { initStoopConnect } from "@/lib/stripeConnect";

type Section = "overview" | "payments" | "account" | "documents";

const SECTIONS: { id: Section; label: string }[] = [
  { id: "overview", label: "Overview" },
  { id: "payments", label: "Payments" },
  { id: "account", label: "Account" },
  { id: "documents", label: "Documents" },
];

export default function SellerPayouts() {
  const [section, setSection] = useState<Section>("overview");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [embedError, setEmbedError] = useState<string | null>(null);
  const [resetKey, setResetKey] = useState(0);

  // Initialize once per mount — fetchClientSecret handles refresh.
  // resetKey bumps to re-init after a retry from the error state.
  const connectInstance = useMemo(() => {
    try {
      return initStoopConnect();
    } catch (err: any) {
      setLoadError(err?.message || "Couldn't initialize Stripe");
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  const handleLoadError = ({ error }: { error: { message?: string }; elementTagName: string }) => {
    setEmbedError(error?.message || "Stripe couldn't load this section.");
  };
  const retry = () => {
    setEmbedError(null);
    setLoadError(null);
    setResetKey((k) => k + 1);
  };
  const embedProps = { onLoadError: handleLoadError };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <div className="container mx-auto w-full max-w-3xl flex-1 px-4 pb-12 pt-3">
        <Link
          href="/seller"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" />
          Back to dashboard
        </Link>
        <h1 className="mt-3 font-display text-2xl font-bold text-foreground">
          Payouts &amp; earnings
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Live balance, payout schedule, and tax documents from Stripe.
        </p>

        <div className="mt-4 flex gap-1 overflow-x-auto rounded-xl bg-muted/60 p-1">
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setSection(s.id)}
              className={
                "flex-1 whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium transition-colors " +
                (section === s.id
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground")
              }
              data-testid={`tab-${s.id}`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {loadError || !connectInstance ? (
          <div className="mt-6 flex items-start gap-3 rounded-2xl border border-destructive/30 bg-destructive/5 p-4">
            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-destructive" />
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-foreground">
                Couldn't load Stripe components
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {loadError ?? "Something went wrong. Please refresh."}
              </p>
              <button
                type="button"
                onClick={retry}
                className="mt-3 inline-flex items-center rounded-lg bg-foreground px-3 py-1.5 text-sm font-semibold text-background hover:opacity-90"
                data-testid="button-retry-stripe"
              >
                Try again
              </button>
            </div>
          </div>
        ) : (
          <ConnectComponentsProvider key={resetKey} connectInstance={connectInstance}>
            <div className="mt-6 space-y-6">
              {embedError && (
                <div className="flex items-start gap-3 rounded-2xl border border-destructive/30 bg-destructive/5 p-4">
                  <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-destructive" />
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-foreground">
                      Stripe couldn't load this section
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {embedError}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={retry}
                        className="inline-flex items-center rounded-lg bg-foreground px-3 py-1.5 text-sm font-semibold text-background hover:opacity-90"
                        data-testid="button-retry-stripe-embed"
                      >
                        Try again
                      </button>
                      <Link
                        href="/seller"
                        className="inline-flex items-center rounded-lg border border-border bg-background px-3 py-1.5 text-sm font-semibold text-foreground hover:bg-accent/40"
                      >
                        Back to dashboard
                      </Link>
                    </div>
                  </div>
                </div>
              )}
              {section === "overview" && (
                <>
                  <SectionCard title="Balance">
                    <ConnectBalances {...embedProps} />
                  </SectionCard>
                  <SectionCard title="Payouts">
                    <ConnectPayouts {...embedProps} />
                  </SectionCard>
                </>
              )}
              {section === "payments" && (
                <SectionCard title="Recent payments">
                  <ConnectPayments {...embedProps} />
                </SectionCard>
              )}
              {section === "account" && (
                <SectionCard title="Account &amp; bank info">
                  <ConnectAccountManagement {...embedProps} />
                </SectionCard>
              )}
              {section === "documents" && (
                <SectionCard title="Tax documents">
                  <ConnectDocuments {...embedProps} />
                </SectionCard>
              )}
            </div>
          </ConnectComponentsProvider>
        )}
      </div>
    </div>
  );
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-border bg-card p-4 shadow-sm">
      <h2 className="mb-3 font-display text-base font-semibold text-foreground">{title}</h2>
      <div className="stripe-connect-host">{children}</div>
    </section>
  );
}
